/**
 * QiltFocusAreaDeepDive — multi-year, multi-cohort QILT SES 5-focus-area
 * radar. Every ring is one QILT SES cycle × cohort. Rings from the
 * SAME cohort overlay as a trend; rings from different cohorts get
 * explicit cohort labels in the legend so the reader isn't misled
 * by side-by-side numbers.
 *
 * When only ONE ring exists we still render the chart (a single ring
 * versus the national benchmark line is genuinely useful) but with
 * a small note explaining that a second ring will appear when more
 * data lands.
 *
 * Never mixes cohorts without a label. Honest-depth-split preserved.
 *
 * Source: /api/methodology/qilt-ses/focus-trend?uni_id=X
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";

// Deterministic colour per cycle so the same year always shows the
// same colour across the two cohort views.
const CYCLE_COLOR = {
  "2022": "#a855f7",
  "2023": "#7c3aed",
  "2024": "#4C1D95",
};
const BENCH_COLOR = "#059669";

const COHORT_LABEL = {
  international_undergraduate: "International UG",
  domestic_undergraduate:      "Domestic UG",
};

export default function QiltFocusAreaDeepDive({ uniId }) {
  const [data, setData] = useState(null);
  const [cohort, setCohort] = useState(null);   // selected cohort tab

  useEffect(() => {
    if (!uniId) return;
    api.get(`/methodology/qilt-ses/focus-trend?uni_id=${uniId}`)
       .then((r) => {
         setData(r.data);
         setCohort(r.data?.preferred_cohort || null);
       })
       .catch(() => setData(null));
  }, [uniId]);

  const availableCohorts = useMemo(() => {
    if (!data?.cohort_ring_counts) return [];
    return Object.keys(data.cohort_ring_counts).sort();
  }, [data]);

  const activeCycles = useMemo(() => {
    if (!data?.cycles || !cohort) return [];
    return data.cycles
      .filter((c) => c.cohort === cohort)
      .sort((a, b) => a.cycle.localeCompare(b.cycle));
  }, [data, cohort]);

  if (!data || !cohort || activeCycles.length === 0) return null;

  // Recharts row shape: one row per focus-area, one column per cycle
  // + benchmark of the most recent cycle.
  const themeLabels = activeCycles[0].themes.map((t) => t.label);
  const rows = themeLabels.map((label, i) => {
    const row = { theme: label };
    activeCycles.forEach((c) => {
      row[c.cycle] = Math.round(c.themes[i].positivity_pct * 10) / 10;
    });
    const last = activeCycles[activeCycles.length - 1];
    if (last.themes[i].benchmark_pct != null) {
      row["National avg"] = Math.round(last.themes[i].benchmark_pct * 10) / 10;
    }
    return row;
  });

  const singleRing = activeCycles.length === 1;

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="qilt-deepdive-chart">
      <div className="flex items-baseline gap-2 flex-wrap mb-1">
        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-700 text-white text-[9px] uppercase tracking-wider font-mono">
          QILT deep-dive
        </span>
        <span className="label-uppercase text-emerald-900">
          Focus-area breakdown · {activeCycles[0].cycle}
          {!singleRing && `→${activeCycles[activeCycles.length - 1].cycle}`}
        </span>
        <span className="text-[10px] font-mono text-stone-600">· {COHORT_LABEL[cohort] || cohort}</span>
      </div>

      {availableCohorts.length > 1 && (
        <div className="flex flex-wrap gap-1 mb-3" data-testid="qilt-deepdive-cohort-tabs">
          {availableCohorts.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setCohort(k)}
              data-testid={`qilt-deepdive-cohort-${k}`}
              className={
                "px-2.5 py-0.5 rounded-full border text-[10px] font-underground-sc tracking-wider transition " +
                (cohort === k
                  ? "bg-emerald-800 text-white border-emerald-800"
                  : "bg-white text-stone-700 border-stone-300 hover:border-emerald-700")
              }
            >
              {COHORT_LABEL[k] || k} · {data.cohort_ring_counts[k]}
            </button>
          ))}
        </div>
      )}

      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <RadarChart data={rows} outerRadius="72%">
            <PolarGrid stroke="#e7e5e4" />
            <PolarAngleAxis dataKey="theme"
                            tick={{ fontSize: 11, fill: "#065f46", fontFamily: "monospace" }} />
            <PolarRadiusAxis angle={90} domain={[50, 100]}
                             tick={{ fontSize: 9, fill: "#a8a29e" }} />
            {activeCycles.map((c) => (
              <Radar
                key={c.cycle}
                name={c.cycle}
                dataKey={c.cycle}
                stroke={CYCLE_COLOR[c.cycle] || "#4C1D95"}
                fill={CYCLE_COLOR[c.cycle] || "#4C1D95"}
                fillOpacity={0.28}
              />
            ))}
            <Radar
              name="National avg"
              dataKey="National avg"
              stroke={BENCH_COLOR}
              fill={BENCH_COLOR}
              fillOpacity={0.10}
              strokeDasharray="4 4"
            />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {singleRing ? (
        <p className="mt-2 text-[11px] text-stone-500 leading-snug" data-testid="qilt-deepdive-single-year-note">
          Only one cycle of {COHORT_LABEL[cohort]?.toLowerCase()} focus-area data has been published for this uni.
          A second ring will appear here automatically when the next QILT SES cycle drops
          (queued in the CMS editorial calendar).
        </p>
      ) : (
        <p className="mt-2 text-[11px] text-stone-500 leading-snug">
          Rings overlay by cycle — same colour = same year. Green dashed line = QILT national average.
          Comparable within a single cohort; if you switched cohort tabs above, the numbers reflect a
          different student cohort (international vs domestic) with different survey mechanics.
        </p>
      )}
    </div>
  );
}
